'use client';
import { interpolate, useTranslation } from '../../lib/i18n';
import type { MemoryMaintenanceJob } from '../../lib/types';
import { Progress } from '../../components/ui/shadcn/progress';

/** One job's durable counters and, while it runs, its meter. The same reading in both places it appears:
 *  the operation window the dock raises, which states the job's own outcome above it, and a record of the
 *  maintenance section, which states the job's state as a chip instead — so `compact` drops the word and
 *  leaves the numbers, the meter and the failure lines untouched. */
export function MemoryMaintenanceProgress({ job, compact = false }: { job: MemoryMaintenanceJob; compact?: boolean }) {
  const { t } = useTranslation();
  const copy = t.memory;
  const label = { idle: copy.maintenanceIdle, running: copy.maintenanceRunning, done: copy.maintenanceDone,
    error: copy.maintenanceError, interrupted: copy.maintenanceInterrupted }[job.status];
  const failed = job.status === 'error' || job.status === 'interrupted' || job.failed > 0;
  const percent = job.total > 0 ? Math.round(job.processed / job.total * 100) : null;
  const counts = interpolate(copy.maintenanceCounts, { succeeded: job.succeeded, failed: job.failed });
  return <div className={compact ? 'flex min-w-0 flex-col gap-1.5' : 'flex flex-col gap-2'} aria-live="polite">
    <div className="flex items-center justify-between gap-3 text-small text-muted-foreground">
      <span role={failed ? 'alert' : 'status'} className="min-w-0">{compact ? counts : label}</span>
      <span className="shrink-0 tabular-nums">{job.processed}/{job.total}</span>
    </div>
    {job.status === 'running' ? <Progress aria-label={t.operationProgress.progressLabel} value={percent}
      aria-valuemin={0} aria-valuemax={job.total || undefined} aria-valuenow={job.total > 0 ? job.processed : undefined}
      indicatorValue={percent === null ? 100 : undefined}
      indicatorClassName={percent === null ? 'operation-progress-indeterminate' : undefined} /> : null}
    {compact ? null : <p className="text-small text-muted-foreground">{counts}</p>}
    {/* A compact record states the job's state in its chip, in the shortest word the vocabulary has — so the
        cause of an interrupted run keeps a line of its own here instead of riding in the chip and wrapping
        it. The full reading above already names it in its own state line. */}
    {compact && job.status === 'interrupted' ? <p className="text-small text-muted-foreground">{label}</p> : null}
    {job.failed > 0 ? <p className="text-small text-warning">{interpolate(copy.maintenanceFailures, { n: job.failed })}</p> : null}
    {job.error ? <p className="break-words text-ui text-destructive">{job.error}</p> : null}
  </div>;
}
